// src/pages/Parametros/tabs/TabDump.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Typography, Paper, Button, Chip, Divider,
    CircularProgress, Alert, Card, CardContent, TextField, LinearProgress,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import {
    getDumpBlockReasons,
    deleteDump,
    getDumpLogs,
    getDumpStatus,
    getNextDumpWindow,
    listDumpJobs,
    pauseDump,
    resumeDump,
    startDump,
} from '../../../infra/api/DumpPipelineApi';

const EMPTY_DISPONIVEL = {
    dataInicio: null,
    dataFim: null,
    totalExperimentos: 0,
    validos: 0,
    invalidos: 0,
};

const STATUS_MAP = {
    concluido:   { label: 'Concluído',    color: 'success' },
    processando: { label: 'Processando',  color: 'warning' },
    em_processamento: { label: 'Em processamento', color: 'warning' },
    pausa_solicitada: { label: 'Pausa solicitada', color: 'warning' },
    pausado: { label: 'Pausado', color: 'info' },
    erro:        { label: 'Erro',         color: 'error'   },
};

function pick(obj, ...keys) {
    for (const k of keys) {
        if (obj?.[k] !== undefined && obj?.[k] !== null) return obj[k];
    }
    return undefined;
}

function fmtDate(iso) {
    const d = parseDateSafe(iso);
    if (!d) return '—';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(iso) {
    const d = parseDateSafe(iso);
    if (!d) return '—';
    return d.toLocaleString('pt-BR');
}

function parseDateSafe(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

function proximoInicioDump(historico, disponivel) {
    if (!disponivel?.dataInicio) return null;
    if (!historico.length) return disponivel.dataInicio;

    const validos = historico.filter((h) => parseDateSafe(h?.dataFim));
    if (!validos.length) return disponivel.dataInicio;

    const ultimo = validos.reduce((a, b) => {
        const da = parseDateSafe(a.dataFim);
        const db = parseDateSafe(b.dataFim);
        return da > db ? a : b;
    });

    const next = parseDateSafe(ultimo.dataFim);
    if (!next) return disponivel.dataInicio;
    next.setDate(next.getDate() + 1);
    return next.toISOString();
}

function StatBox({ label, value, color }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 110, textAlign: 'center', borderColor: color || 'divider' }}>
            <Typography variant="caption" color={color || 'text.secondary'} display="block">
                {label}
            </Typography>
            <Typography variant="h6" fontWeight={700} color={color || 'text.primary'}>
                {value ?? '—'}
            </Typography>
        </Paper>
    );
}

// converte ISO para formato input date (YYYY-MM-DD)
function toInputDate(iso) {
    const d = parseDateSafe(iso);
    if (!d) return '';
    return d.toISOString().slice(0, 10);
}

// converte input date (YYYY-MM-DD) para ISO
function fromInputDate(str) {
    if (!str) return '';
    const d = new Date(`${str}T00:00:00.000Z`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
}

function currentTenant() {
    const [, maybe] = window.location.pathname.split('/');
    return maybe || null;
}

function wsBaseUrl() {
    const httpBase = process.env.REACT_APP_DUMP_PIPE_URL ?? 'http://localhost:8085';
    const parsed = new URL(httpBase);
    parsed.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    parsed.pathname = '';
    return parsed.toString().replace(/\/$/, '');
}

export default function TabDump() {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [processando, setProcessando] = useState(false);
    const [historico, setHistorico] = useState([]);
    const [disponivel, setDisponivel] = useState(EMPTY_DISPONIVEL);
    const [erro, setErro] = useState('');
    const [blockInfo, setBlockInfo] = useState(null);
    const [statusDump, setStatusDump] = useState({ hasDump: false, hasActive: false });
    const [dumpLogs, setDumpLogs] = useState([]);
    const [acaoJobLoading, setAcaoJobLoading] = useState(false);
    const [deleteLoadingId, setDeleteLoadingId] = useState('');
    const [rangeTouched, setRangeTouched] = useState(false);
    const wsRef = useRef(null);

    const defaultInicio = proximoInicioDump(historico, disponivel);

    const [dataInicio, setDataInicio] = useState(toInputDate(defaultInicio));
    const [dataFim, setDataFim]       = useState(toInputDate(disponivel.dataFim));

    const temNovosDados = useMemo(() => {
        if (!dataInicio || !dataFim) return false;
        const inicio = parseDateSafe(`${dataInicio}T00:00:00.000Z`);
        const fim = parseDateSafe(`${dataFim}T00:00:00.000Z`);
        if (!inicio || !fim) return false;
        return inicio <= fim;
    }, [dataInicio, dataFim]);

    const carregarDados = async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setErro('');
        }
        try {
            const [status, window, jobs] = await Promise.all([
                getDumpStatus(),
                getNextDumpWindow(),
                listDumpJobs(),
            ]);

            setStatusDump(status || { hasDump: false, hasActive: false });
            const jobsMap = Array.isArray(jobs) ? jobs.map((j) => ({
                id: pick(j, 'id', '_id', 'ID') || '',
                dataInicio: pick(j, 'dataInicio', 'DataInicio') || null,
                dataFim: pick(j, 'dataFim', 'DataFim') || null,
                criadoPor: pick(j, 'criadoPor', 'CriadoPor') || 'sistema',
                criadoEm: pick(j, 'criadoEm', 'CriadoEm') || null,
                totalExperimentos: Number(pick(j, 'total', 'Total') || 0),
                processados: Number(pick(j, 'processados', 'Processados') || 0),
                validos: Number(pick(j, 'validos', 'Validos') || 0),
                invalidos: Number(pick(j, 'invalidos', 'Invalidos') || 0),
                status: pick(j, 'status', 'Status') || 'concluido',
            })) : [];
            setHistorico(jobsMap);

            const ativo = jobsMap.find((j) => j.status === 'em_processamento' || j.status === 'pausado' || j.status === 'pausa_solicitada');
            if (ativo?.id) {
                try {
                    const logs = await getDumpLogs(ativo.id);
                    setDumpLogs(Array.isArray(logs) ? logs : []);
                } catch {
                    setDumpLogs([]);
                }
            } else {
                setDumpLogs([]);
            }

            const inicio = window?.suggestedStart || null;
            const fim = window?.suggestedEnd || null;
            setDisponivel({
                ...EMPTY_DISPONIVEL,
                dataInicio: inicio,
                dataFim: fim,
            });
            if (!rangeTouched) {
                setDataInicio(toInputDate(inicio));
                setDataFim(toInputDate(fim));
            }
        } catch (e) {
            if (!silent) {
                setErro(e?.response?.data?.error || e?.message || 'Erro ao carregar dados de dump');
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        carregarDados();
    }, []);

    useEffect(() => {
        if (!statusDump?.hasActive) {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            return undefined;
        }

        const tenant = currentTenant();
        if (!tenant) return undefined;

        const ws = new WebSocket(`${wsBaseUrl()}/ws/${tenant}`);
        wsRef.current = ws;

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data || '{}');
                const msgJobId = pick(msg, 'jobId', 'JobID');
                if (!msgJobId) return;

                const msgStatus = pick(msg, 'status', 'Status') || 'em_processamento';
                const msgTotal = Number(pick(msg, 'total', 'Total') || 0);
                const msgProcessados = Number(pick(msg, 'processados', 'Processados') || 0);
                const msgText = pick(msg, 'msg', 'Msg');

                setHistorico((prev) => prev.map((job) => {
                    if (job.id !== msgJobId) return job;
                    return {
                        ...job,
                        status: msgStatus,
                        totalExperimentos: msgTotal > 0 ? msgTotal : job.totalExperimentos,
                        processados: msgProcessados,
                    };
                }));

                if (msgText) {
                    setDumpLogs((prev) => [
                        {
                            id: `ws-${Date.now()}`,
                            createdAt: new Date().toISOString(),
                            level: 'info',
                            message: msgText,
                        },
                        ...prev,
                    ].slice(0, 300));
                }

                const stillActive = msgStatus === 'em_processamento' || msgStatus === 'pausa_solicitada';
                setStatusDump((prev) => ({ ...prev, hasActive: stillActive }));

                if (!stillActive) {
                    carregarDados({ silent: true });
                }
            } catch {
                // ignora mensagens não parseáveis
            }
        };

        return () => {
            ws.close();
            if (wsRef.current === ws) {
                wsRef.current = null;
            }
        };
    }, [statusDump?.hasActive]);

    useEffect(() => {
        const checkBlock = async () => {
            if (!dataInicio || !dataFim) {
                setBlockInfo(null);
                return;
            }
            try {
                const block = await getDumpBlockReasons(fromInputDate(dataInicio), fromInputDate(dataFim));
                setBlockInfo(block);
            } catch {
                setBlockInfo(null);
            }
        };
        checkBlock();
    }, [dataInicio, dataFim]);

    const handleProcessarDump = async () => {
        setErro('');
        setProcessando(true);
        try {
            await startDump(fromInputDate(dataInicio), fromInputDate(dataFim));
            await carregarDados({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao iniciar dump');
        } finally {
            setProcessando(false);
        }
    };

    const jobAtivo = historico.find((h) => h.status === 'em_processamento' || h.status === 'pausado' || h.status === 'pausa_solicitada');
    const progressoPct = jobAtivo?.totalExperimentos > 0
        ? Math.min(100, Math.round((jobAtivo.processados / jobAtivo.totalExperimentos) * 100))
        : 0;

    const handlePause = async () => {
        if (!jobAtivo?.id) return;
        setAcaoJobLoading(true);
        try {
            await pauseDump(jobAtivo.id);
            await carregarDados({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao pausar dump');
        } finally {
            setAcaoJobLoading(false);
        }
    };

    const handleResume = async () => {
        if (!jobAtivo?.id) return;
        setAcaoJobLoading(true);
        try {
            await resumeDump(jobAtivo.id);
            await carregarDados({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao retomar dump');
        } finally {
            setAcaoJobLoading(false);
        }
    };

    const handleDeleteDump = async (jobId) => {
        if (!jobId) return;
        const ok = window.confirm('Tem certeza que deseja excluir este dump? Isso apaga banco e pasta em disco (se existir).');
        if (!ok) return;
        setDeleteLoadingId(jobId);
        setErro('');
        try {
            await deleteDump(jobId);
            await carregarDados({ silent: true });
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao excluir dump');
        } finally {
            setDeleteLoadingId('');
        }
    };

    const bloqueado = Boolean(statusDump?.hasActive) || Boolean(blockInfo?.blocked);
    const statsNovoDump = jobAtivo
        ? {
            totalExperimentos: jobAtivo.totalExperimentos || 0,
            validos: jobAtivo.validos || 0,
            invalidos: jobAtivo.invalidos || 0,
        }
        : disponivel;
    const blockMessages = Array.isArray(blockInfo?.reasons)
        ? blockInfo.reasons
            .filter((r) => !(statusDump?.hasActive && r?.code === 'active_dump'))
            .map((r) => r.message)
            .filter(Boolean)
        : [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
            {loading && <Alert severity="info">Carregando dados de dump…</Alert>}
            {erro && <Alert severity="error">{erro}</Alert>}

            {/* ── Novo Dump ── */}
            <Paper variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Lucide.Database color={theme.palette.primary.main} />
                    <Typography variant="subtitle1" fontWeight={700}>Novo Dump</Typography>
                </Box>

                {statusDump?.hasActive && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Já existe um dump em processamento para este tenant.
                    </Alert>
                )}

                {historico.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Último dump encerrado em <strong>{fmtDate(historico[0]?.dataFim)}</strong>.
                        Datas preenchidas automaticamente — ajuste se necessário.
                    </Alert>
                )}

                {blockInfo?.blocked && blockMessages.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {blockMessages.join(' | ')}
                    </Alert>
                )}

                {jobAtivo && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight={700}>
                                Job {jobAtivo.id} - {STATUS_MAP[jobAtivo.status]?.label || jobAtivo.status}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handlePause}
                                    disabled={acaoJobLoading || jobAtivo.status !== 'em_processamento'}
                                >
                                    Pausar
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleResume}
                                    disabled={acaoJobLoading || (jobAtivo.status !== 'pausado' && jobAtivo.status !== 'pausa_solicitada')}
                                >
                                    Retomar
                                </Button>
                            </Box>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <LinearProgress variant="determinate" value={progressoPct} />
                            <Typography variant="caption" color="text.secondary">
                                {jobAtivo.processados || 0}/{jobAtivo.totalExperimentos || 0} ({progressoPct}%)
                            </Typography>
                        </Box>
                    </Paper>
                )}

                {/* Seletores de período editáveis */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
                    <TextField
                        label="Data início"
                        type="date"
                        value={dataInicio}
                        onChange={(e) => {
                            setRangeTouched(true);
                            setDataInicio(e.target.value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 200 }}
                    />
                    <TextField
                        label="Data fim"
                        type="date"
                        value={dataFim}
                        onChange={(e) => {
                            setRangeTouched(true);
                            setDataFim(e.target.value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: dataInicio }}
                        sx={{ width: 200 }}
                    />
                    <StatBox label="Total"     value={statsNovoDump.totalExperimentos} />
                    <StatBox label="Válidos"   value={statsNovoDump.validos}    color="success.main" />
                    <StatBox label="Inválidos" value={statsNovoDump.invalidos}  color="error.main" />
                </Box>

                <Button
                    variant="contained"
                    startIcon={
                        processando
                            ? <CircularProgress size={16} color="inherit" />
                            : <Lucide.Download />
                    }
                    onClick={handleProcessarDump}
                    disabled={processando || !temNovosDados || bloqueado || loading}
                >
                    {processando ? 'Processando…' : 'Processar Dump'}
                </Button>
                {!temNovosDados && dataInicio && dataFim && (
                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                        Data início deve ser anterior ao fim
                    </Typography>
                )}
            </Paper>

            {/* ── Histórico ── */}
            <Box>
                <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    Histórico de Dumps
                </Typography>

                {historico.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum dump realizado ainda.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {historico.map((dump) => {
                            const st = STATUS_MAP[dump.status] ?? STATUS_MAP.concluido;
                            return (
                                <Card key={dump.id} variant="outlined">
                                    <CardContent>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            mb: 1,
                                        }}>
                                            <Typography variant="body1" fontWeight={700}>
                                                {fmtDate(dump.dataInicio)} → {fmtDate(dump.dataFim)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Chip label={st.label} color={st.color} size="small" />
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteDump(dump.id)}
                                                    disabled={deleteLoadingId === dump.id || dump.status === 'em_processamento'}
                                                >
                                                    {deleteLoadingId === dump.id ? 'Excluindo…' : 'Excluir'}
                                                </Button>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 1 }} />

                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Total</Typography>
                                                <Typography variant="body2" fontWeight={700}>{dump.totalExperimentos}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="success.main">Válidos</Typography>
                                                <Typography variant="body2" fontWeight={700} color="success.main">{dump.validos}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="error.main">Inválidos</Typography>
                                                <Typography variant="body2" fontWeight={700} color="error.main">{dump.invalidos}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Criado por</Typography>
                                                <Typography variant="body2">{dump.criadoPor}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Processado em</Typography>
                                                <Typography variant="body2">{fmtDateTime(dump.criadoEm)}</Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {jobAtivo && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Logs do Job</Typography>
                    <Box sx={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {dumpLogs.length === 0 && (
                            <Typography variant="caption" color="text.secondary">Sem logs ainda.</Typography>
                        )}
                        {dumpLogs.map((l, idx) => (
                            <Typography key={`${l.ID || l.id || idx}`} variant="caption" sx={{ fontFamily: 'monospace' }}>
                                [{fmtDateTime(l.CreatedAt || l.createdAt)}] {l.Level || l.level}: {l.Message || l.message}
                            </Typography>
                        ))}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
